import { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { adminApi } from '../lib/api';

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#64748B]">{label}</div>
      <div className="font-display text-2xl font-bold text-[#0A1F44] mt-1.5">{value}</div>
    </div>
  );
}

export default function AdminTrafikk() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = () => adminApi.get('/admin/traffic', { params: { days } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  if (!data) return <div className="text-[#64748B]">Laster…</div>;
  const { kpis, daily, top_paths, top_searches, categories, clinics, funnel, referrers } = data;

  const dropStart = funnel.visits > 0 ? Math.round((1 - funnel.form_starts / funnel.visits) * 100) : 0;
  const dropComplete = funnel.form_starts > 0 ? Math.round((1 - funnel.completed / funnel.form_starts) * 100) : 0;

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold text-[#0A1F44]">Trafikk</h1>
        <div className="flex gap-1">
          {[7, 30, 90].map((n) => (
            <button key={n} onClick={() => setDays(n)} className={`px-3 py-1.5 text-sm rounded-lg ${days === n ? 'bg-[#0A1F44] text-white' : 'bg-white border border-[#0A1F44]/10 text-[#0A1F44]'}`} data-testid={`trafikk-days-${n}`}>
              {n}d
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi label="Sidebesøk" value={kpis.views} testid="t-kpi-views" />
        <Kpi label="Unike sesjoner" value={kpis.unique_sessions} />
        <Kpi label="Sider/sesjon" value={kpis.pages_per_session} />
        <Kpi label="Skjemastart" value={kpis.form_starts} />
        <Kpi label="Fullførte" value={kpis.completed} />
        <Kpi label="Konvertering" value={`${kpis.conversion}%`} testid="t-kpi-conversion" />
      </div>

      <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
        <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Daglige sesjoner</h2>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={daily}>
            <defs>
              <linearGradient id="fillA" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0A1F44" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#0A1F44" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" stroke="#64748B" fontSize={10} />
            <YAxis stroke="#64748B" fontSize={11} />
            <Tooltip />
            <Area dataKey="sessions" stroke="#0A1F44" strokeWidth={2} fill="url(#fillA)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
          <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Mest besøkte sider</h2>
          <table className="w-full text-sm">
            <tbody>
              {top_paths.length === 0 && <tr><td className="text-[#64748B] py-4">Ingen data</td></tr>}
              {top_paths.map((r, i) => (
                <tr key={i} className="border-b border-[#0A1F44]/5 last:border-0">
                  <td className="py-2 font-mono text-[12px] text-[#0A1F44]">{r.path}</td>
                  <td className="text-right text-[#334155]">{r.views} ({r.pct}%)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
          <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Søkeanalyse</h2>
          <p className="text-xs text-[#64748B] mb-3">Hva søker pasientene etter? Informerer innhold og klinikk-akkvisisjon.</p>
          <table className="w-full text-sm">
            <tbody>
              {top_searches.length === 0 && <tr><td className="text-[#64748B] py-4">Ingen søk ennå</td></tr>}
              {top_searches.map((r, i) => (
                <tr key={i} className="border-b border-[#0A1F44]/5 last:border-0">
                  <td className="py-2 text-[#0A1F44]">{r.query}</td>
                  <td className="text-right text-[#334155]">{r.count}×</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
        <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Kategori-popularitet</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-[#64748B] text-left">
                <th className="pb-2">Kategori</th><th>Besøk</th><th>Leads</th><th className="text-right">Konvertering</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((r, i) => (
                <tr key={i} className="border-t border-[#0A1F44]/5">
                  <td className="py-2 text-[#0A1F44] font-medium">{r.category}</td>
                  <td>{r.views}</td>
                  <td>{r.leads}</td>
                  <td className="text-right text-[#3B82F6] font-semibold">{r.conversion}%</td>
                </tr>
              ))}
              {categories.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-[#64748B]">Ingen data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
        <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-5">Skjema-funnel</h2>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center bg-[#FAF8F5] rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#64748B] mb-1">Sesjoner</div>
            <div className="font-display text-3xl font-bold text-[#0A1F44]">{funnel.visits}</div>
          </div>
          <div className="text-center bg-[#FAF8F5] rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#64748B] mb-1">Skjemastart</div>
            <div className="font-display text-3xl font-bold text-[#0A1F44]">{funnel.form_starts}</div>
            <div className="text-xs text-red-600 mt-1">-{dropStart}%</div>
          </div>
          <div className="text-center bg-[#3B82F6] text-white rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-white/70 mb-1">Fullført</div>
            <div className="font-display text-3xl font-bold">{funnel.completed}</div>
            <div className="text-xs text-white/80 mt-1">-{dropComplete}%</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
        <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Henvisende kilder</h2>
        <table className="w-full text-sm">
          <tbody>
            {referrers.length === 0 && <tr><td className="py-4 text-[#64748B]">Ingen data</td></tr>}
            {referrers.map((r, i) => (
              <tr key={i} className="border-b border-[#0A1F44]/5 last:border-0">
                <td className="py-2 text-[#0A1F44]">{r.source}</td>
                <td className="text-right text-[#334155]">{r.sessions} ({r.pct}%)</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
